import { HourOfDay } from "@swivl-mono/types";
import { DateTime } from "luxon";

export class DateHelper {
  static toIsoString(date: string | Date | null | undefined): string | null {
    if (date == null) return null;

    if (date instanceof Date) {
      return date.toISOString();
    }

    return date ? String(date) : (date as string);
  }

  static timeAgoShort(from: string | Date): string {
    const dt = from instanceof Date ? from : new Date(from);
    const sec = Math.max(0, Math.floor((Date.now() - dt.getTime()) / 1000));
    if (sec < 60) return `${sec}s`;
    const min = Math.floor(sec / 60);
    if (min < 60) return `${min}m`;
    const hr = Math.floor(min / 60);
    if (hr < 24) return `${hr}h`;
    const d = Math.floor(hr / 24);
    return `${d}d`;
  }

  static time(date: Date | string | number | undefined): number {
    if (!date) return Date.now();
    if (typeof date === "string") return new Date(date).getTime();
    if (typeof date === "number") return date;
    return date.getTime();
  }

  static normalize(date: Date | string | number | undefined): Date {
    if (!date) return new Date();
    if (typeof date === "string") return new Date(date);
    if (typeof date === "number") return new Date(date);
    return date;
  }
  static dateTime(date: Date | string | number | undefined): DateTime {
    return DateTime.fromJSDate(DateHelper.normalize(date));
  }
  static dateString(timezone?: string): string {
    if (timezone) {
      let datestring = DateTime.now().setZone(timezone).toISODate();
      if (datestring) return datestring;
    }
    return DateTime.now().toISODate();
  }

  static nowHourUTC(): HourOfDay {
    return DateHelper.getRoundedTime(DateTime.utc());
  }

  static isNowInRange(start: HourOfDay, end: HourOfDay): boolean {
    return DateHelper.isHourInRange(DateHelper.nowHourUTC(), start, end);
  }

  static nowHourInZone(timezone?: string): HourOfDay {
    const dt = timezone ? DateTime.now().setZone(timezone) : DateTime.now();
    return DateHelper.getRoundedTime(dt);
  }

  private static getRoundedTime(dt: DateTime) {
    const hhmm = dt.toFormat("HH:mm");
    const [hours, minutes] = hhmm.split(":");
    let minutesRounded = "00";
    if (parseInt(minutes) >= 30) {
      minutesRounded = "30";
    }
    return `${hours}:${minutesRounded}` as HourOfDay;
  }

  static isHourInRange(nowHour: HourOfDay, start: HourOfDay, end: HourOfDay): boolean {
    const [hourStart, minuteStart] = start.split(":");
    const [hourEnd, minuteEnd] = end.split(":");
    const [hour, minute] = nowHour.split(":");

    const startMinutes = parseInt(hourStart) * 60 + parseInt(minuteStart);
    const endMinutes = parseInt(hourEnd) * 60 + parseInt(minuteEnd);
    const minutes = parseInt(hour) * 60 + parseInt(minute);
    if (startMinutes <= endMinutes) {
      return minutes >= startMinutes && minutes < endMinutes;
    }

    if (minutes === endMinutes) {
      return true;
    }
    if (minutes === startMinutes) {
      return false;
    }
    return !(minutes >= endMinutes && minutes < startMinutes);
  }

  static fixDates<T>(obj: T, fieldsToFix?: string[]): T {
    const dateFields = new Set(
      fieldsToFix || ["lastInteractionAt", "lastUserMessageAt", "createdAt", "updatedAt", "searchingForNextAgentAt", "lastMessageAt"]
    );

    const isValidDate = (v: unknown): v is Date => v instanceof Date && !isNaN(v.getTime());

    const toDateOrUndefined = (v: unknown): Date | undefined => {
      if (isValidDate(v)) return v;
      // Allow numbers (epoch ms) and strings (ISO or Date.parse-able)
      const d = new Date(v as any);
      return isNaN(d.getTime()) ? undefined : d;
    };

    const isPlainObject = (v: unknown): v is Record<string, unknown> => typeof v === "object" && v !== null && !Array.isArray(v);

    const normalizeInPlace = (value: unknown): unknown => {
      if (Array.isArray(value)) {
        for (let i = 0; i < value.length; i++) {
          const el = value[i];
          // Only recurse into objects/arrays; primitives stay as-is
          if (isPlainObject(el) || Array.isArray(el)) {
            value[i] = normalizeInPlace(el);
          }
        }
        return value;
      }

      if (isPlainObject(value)) {
        for (const key of Object.keys(value)) {
          const curr = value[key];
          if (curr === undefined || curr === null) {
            continue;
          }
          if (dateFields.has(key)) {
            // Ensure undefined or a valid Date
            value[key] = toDateOrUndefined(curr);
          } else if (isPlainObject(curr) || Array.isArray(curr)) {
            value[key] = normalizeInPlace(curr);
          }
        }
        return value;
      }

      // Primitive or function, leave as-is
      return value;
    };

    return normalizeInPlace(obj) as T;
  }

  // static fixDates<T>(items: any | any[]): T {
  //   const fieldsToFix = ["lastInteractionAt", "lastUserMessageAt", "createdAt", "updatedAt", "searchingForNextAgentAt", "lastMessageAt"];

  //   if (items && !Array.isArray(items)) {
  //     for (const field of fieldsToFix) {
  //       if (items[field]) {
  //         if (typeof items[field] === "string") {
  //           items[field] = new Date(items[field]);
  //         } else if (!(items[field] instanceof Date)) {
  //           items[field] = new Date(items[field]);
  //         }
  //       }
  //     }
  //     return items as T;
  //   }

  //   return items.map((item: any) => {
  //     for (const field of fieldsToFix) {
  //       if (item[field]) {
  //         if (typeof item[field] === "string") {
  //           item[field] = new Date(item[field]);
  //         } else if (!(item[field] instanceof Date)) {
  //           item[field] = new Date(item[field]);
  //         }
  //       }
  //     }
  //     return item as T;
  //   });
  // }

  // static fixObjectDates(object: any): any {
  //   // Itterate through an object and convert all date strings to Date objects
  //   if (object === null || typeof object !== "object") {
  //     return object; // Return the value if it's not an object
  //   }
  //   if (Array.isArray(object)) {
  //     return object.map((item) => DateHelper.fixObjectDates(item)); // Recursively process arrays
  //   }
  //   const fixedObject: any = {};
  //   for (const key in object) {
  //     if (object.hasOwnProperty(key)) {
  //       const value = object[key];
  //       if (typeof value === "string" && !isNaN(Date.parse(value))) {
  //         fixedObject[key] = new Date(value); // Convert date strings to Date objects
  //       } else {
  //         fixedObject[key] = DateHelper.fixObjectDates(value); // Recursively process nested objects
  //       }
  //     }
  //   }
  //   return fixedObject; // Return the fixed object
  // }

  static minutesToTime(inputMinutes: number) {
    var period;
    var minutes: any = inputMinutes % 60;
    if (minutes === 0) {
      minutes = "00";
    }
    var hours: any = (inputMinutes - minutes) / 60;
    if (hours > 12) {
      hours -= 12;
      period = "PM";
    } else if (hours === 12) {
      period = "PM";
    } else if (hours === 0) {
      hours = "12";
      period = "AM";
    } else if (hours === 24) {
      period = "AM";
    } else {
      period = "AM";
    }

    return hours + ":" + minutes + " " + period;
  }

  /**
   * Formats a date and time for display in MM/DD/YY h:mm AM/PM format
   * @param date - Date, string, or number to format
   * @returns Formatted date string (e.g., "01/26/26 3:45 PM")
   */
  static formatDateTime(date: Date | string | number | undefined): string {
    if (!date) return "";
    const dt = DateHelper.dateTime(date);
    if (!dt.isValid) return "";
    return dt.toFormat("MM/dd/yy h:mm a");
  }

  /**
   * Formats a date for display in MM/DD/YYYY format
   * @param date - Date, string, or number to format
   * @param timezone - Optional IANA zone (e.g. "America/Edmonton"). When omitted, uses the server's local zone. Empty string or invalid zone names fall back to UTC.
   * @returns Formatted date string (e.g., "01/26/2026")
   */
  static formatDateMMDDYYYY(date: Date | string | number | undefined, timezone?: string): string {
    if (!date) return "";
    let dt = DateHelper.dateTime(date);
    if (!dt.isValid) return "";
    if (timezone !== undefined) {
      const zoned = dt.setZone(timezone || "UTC");
      dt = zoned.isValid ? zoned : dt.setZone("UTC");
    }
    return dt.toFormat("MM/dd/yyyy");
  }

  /**
   * Formats a date for display in MM/DD/YYYY <TZ-abbreviation> format (e.g. "05/31/2026 PDT").
   * Falls back to UTC `MM/dd/yyyy` (no abbreviation suffix) when the supplied IANA zone is invalid.
   * Picks a locale based on the IANA region so brand abbreviations like AEST/NZST come through
   * via `Intl` instead of degrading to `GMT+N`.
   */
  static formatDateMMDDYYYYWithTzAbbrev(date: Date | string | number | undefined, timezone: string): string {
    if (!date) return "";
    const dt = DateHelper.dateTime(date);
    if (!dt.isValid) return "";
    const zoned = dt.setZone(timezone);
    if (!zoned.isValid) return DateHelper.formatDateMMDDYYYY(date, "UTC");
    return zoned.setLocale(DateHelper.localeForZone(timezone)).toFormat("MM/dd/yyyy ZZZZ");
  }

  /**
   * Pick a locale whose `Intl` data recognizes the brand abbreviation for this IANA zone. Each
   * locale's `timeZoneName: "short"` only knows the zones in its own region — `en-US` only knows
   * North American abbreviations, `en-AU` only knows Australian ones, etc. For zones outside the
   * chosen locale's data, `Intl` falls back to `GMT+N`. We keep this map English-locale-only so
   * output stays ASCII and English regardless of region; new English-speaking customer regions
   * get a one-line entry here.
   *
   * NB: `Pacific/Auckland` is UTC+12 — outside our noon-UTC anchor's ±11h safe band — so dates
   * stored at noon UTC render on the *next* calendar day in Auckland. The abbreviation (NZST/NZDT)
   * is still correct; the calendar-day roll is a known artifact of the storage scheme.
   */
  private static localeForZone(zone: string): string {
    if (zone.startsWith("Australia/")) return "en-AU";
    if (zone === "Pacific/Auckland" || zone === "Pacific/Chatham") return "en-NZ";
    // en-GB covers BST + continental EU brand names (CEST/CET, EEST/EET, WEST/WET) for any zone
    // whose abbreviation it recognises; falls back to GMT+N for Moscow/Istanbul/Dublin which is
    // acceptable.
    if (zone.startsWith("Europe/")) return "en-GB";
    if (zone === "Asia/Kolkata") return "en-IN";
    if (zone === "Africa/Johannesburg") return "en-ZA";
    return "en-US";
  }
}

export const Timezones = [
  "Europe/Andorra",
  "Asia/Dubai",
  "Asia/Kabul",
  "Europe/Tirane",
  "Asia/Yerevan",
  "Antarctica/Casey",
  "Antarctica/Davis",
  "Antarctica/DumontDUrville", // https://bugs.chromium.org/p/chromium/issues/detail?id=928068
  "Antarctica/Mawson",
  "Antarctica/Palmer",
  "Antarctica/Rothera",
  "Antarctica/Syowa",
  "Antarctica/Troll",
  "Antarctica/Vostok",
  "America/Argentina/Buenos_Aires",
  "America/Argentina/Cordoba",
  "America/Argentina/Salta",
  "America/Argentina/Jujuy",
  "America/Argentina/Tucuman",
  "America/Argentina/Catamarca",
  "America/Argentina/La_Rioja",
  "America/Argentina/San_Juan",
  "America/Argentina/Mendoza",
  "America/Argentina/San_Luis",
  "America/Argentina/Rio_Gallegos",
  "America/Argentina/Ushuaia",
  "Pacific/Pago_Pago",
  "Europe/Vienna",
  "Australia/Lord_Howe",
  "Antarctica/Macquarie",
  "Australia/Hobart",
  "Australia/Currie",
  "Australia/Melbourne",
  "Australia/Sydney",
  "Australia/Broken_Hill",
  "Australia/Brisbane",
  "Australia/Lindeman",
  "Australia/Adelaide",
  "Australia/Darwin",
  "Australia/Perth",
  "Australia/Eucla",
  "Asia/Baku",
  "America/Barbados",
  "Asia/Dhaka",
  "Europe/Brussels",
  "Europe/Sofia",
  "Atlantic/Bermuda",
  "Asia/Brunei",
  "America/La_Paz",
  "America/Noronha",
  "America/Belem",
  "America/Fortaleza",
  "America/Recife",
  "America/Araguaina",
  "America/Maceio",
  "America/Bahia",
  "America/Sao_Paulo",
  "America/Campo_Grande",
  "America/Cuiaba",
  "America/Santarem",
  "America/Porto_Velho",
  "America/Boa_Vista",
  "America/Manaus",
  "America/Eirunepe",
  "America/Rio_Branco",
  "America/Nassau",
  "Asia/Thimphu",
  "Europe/Minsk",
  "America/Belize",
  "America/St_Johns",
  "America/Halifax",
  "America/Glace_Bay",
  "America/Moncton",
  "America/Goose_Bay",
  "America/Blanc-Sablon",
  "America/Toronto",
  "America/Nipigon",
  "America/Thunder_Bay",
  "America/Iqaluit",
  "America/Pangnirtung",
  "America/Atikokan",
  "America/Winnipeg",
  "America/Rainy_River",
  "America/Resolute",
  "America/Rankin_Inlet",
  "America/Regina",
  "America/Swift_Current",
  "America/Edmonton",
  "America/Cambridge_Bay",
  "America/Yellowknife",
  "America/Inuvik",
  "America/Creston",
  "America/Dawson_Creek",
  "America/Fort_Nelson",
  "America/Vancouver",
  "America/Whitehorse",
  "America/Dawson",
  "Indian/Cocos",
  "Europe/Zurich",
  "Africa/Abidjan",
  "Pacific/Rarotonga",
  "America/Santiago",
  "America/Punta_Arenas",
  "Pacific/Easter",
  "Asia/Shanghai",
  "Asia/Urumqi",
  "America/Bogota",
  "America/Costa_Rica",
  "America/Havana",
  "Atlantic/Cape_Verde",
  "America/Curacao",
  "Indian/Christmas",
  "Asia/Nicosia",
  "Asia/Famagusta",
  "Europe/Prague",
  "Europe/Berlin",
  "Europe/Copenhagen",
  "America/Santo_Domingo",
  "Africa/Algiers",
  "America/Guayaquil",
  "Pacific/Galapagos",
  "Europe/Tallinn",
  "Africa/Cairo",
  "Africa/El_Aaiun",
  "Europe/Madrid",
  "Africa/Ceuta",
  "Atlantic/Canary",
  "Europe/Helsinki",
  "Pacific/Fiji",
  "Atlantic/Stanley",
  "Pacific/Chuuk",
  "Pacific/Pohnpei",
  "Pacific/Kosrae",
  "Atlantic/Faroe",
  "Europe/Paris",
  "Europe/London",
  "Asia/Tbilisi",
  "America/Cayenne",
  "Africa/Accra",
  "Europe/Gibraltar",
  "America/Godthab",
  "America/Danmarkshavn",
  "America/Scoresbysund",
  "America/Thule",
  "Europe/Athens",
  "Atlantic/South_Georgia",
  "America/Guatemala",
  "Pacific/Guam",
  "Africa/Bissau",
  "America/Guyana",
  "Asia/Hong_Kong",
  "America/Tegucigalpa",
  "America/Port-au-Prince",
  "Europe/Budapest",
  "Asia/Jakarta",
  "Asia/Pontianak",
  "Asia/Makassar",
  "Asia/Jayapura",
  "Europe/Dublin",
  "Asia/Jerusalem",
  "Asia/Kolkata",
  "Indian/Chagos",
  "Asia/Baghdad",
  "Asia/Tehran",
  "Atlantic/Reykjavik",
  "Europe/Rome",
  "America/Jamaica",
  "Asia/Amman",
  "Asia/Tokyo",
  "Africa/Nairobi",
  "Asia/Bishkek",
  "Pacific/Tarawa",
  "Pacific/Enderbury",
  "Pacific/Kiritimati",
  "Asia/Pyongyang",
  "Asia/Seoul",
  "Asia/Almaty",
  "Asia/Qyzylorda",
  "Asia/Qostanay", // https://bugs.chromium.org/p/chromium/issues/detail?id=928068
  "Asia/Aqtobe",
  "Asia/Aqtau",
  "Asia/Atyrau",
  "Asia/Oral",
  "Asia/Beirut",
  "Asia/Colombo",
  "Africa/Monrovia",
  "Europe/Vilnius",
  "Europe/Luxembourg",
  "Europe/Riga",
  "Africa/Tripoli",
  "Africa/Casablanca",
  "Europe/Monaco",
  "Europe/Chisinau",
  "Pacific/Majuro",
  "Pacific/Kwajalein",
  "Asia/Yangon",
  "Asia/Ulaanbaatar",
  "Asia/Hovd",
  "Asia/Choibalsan",
  "Asia/Macau",
  "America/Martinique",
  "Europe/Malta",
  "Indian/Mauritius",
  "Indian/Maldives",
  "America/Mexico_City",
  "America/Cancun",
  "America/Merida",
  "America/Monterrey",
  "America/Matamoros",
  "America/Mazatlan",
  "America/Chihuahua",
  "America/Ojinaga",
  "America/Hermosillo",
  "America/Tijuana",
  "America/Bahia_Banderas",
  "Asia/Kuala_Lumpur",
  "Asia/Kuching",
  "Africa/Maputo",
  "Africa/Windhoek",
  "Pacific/Noumea",
  "Pacific/Norfolk",
  "Africa/Lagos",
  "America/Managua",
  "Europe/Amsterdam",
  "Europe/Oslo",
  "Asia/Kathmandu",
  "Pacific/Nauru",
  "Pacific/Niue",
  "Pacific/Auckland",
  "Pacific/Chatham",
  "America/Panama",
  "America/Lima",
  "Pacific/Tahiti",
  "Pacific/Marquesas",
  "Pacific/Gambier",
  "Pacific/Port_Moresby",
  "Pacific/Bougainville",
  "Asia/Manila",
  "Asia/Karachi",
  "Europe/Warsaw",
  "America/Miquelon",
  "Pacific/Pitcairn",
  "America/Puerto_Rico",
  "Asia/Gaza",
  "Asia/Hebron",
  "Europe/Lisbon",
  "Atlantic/Madeira",
  "Atlantic/Azores",
  "Pacific/Palau",
  "America/Asuncion",
  "Asia/Qatar",
  "Indian/Reunion",
  "Europe/Bucharest",
  "Europe/Belgrade",
  "Europe/Kaliningrad",
  "Europe/Moscow",
  "Europe/Simferopol",
  "Europe/Kirov",
  "Europe/Astrakhan",
  "Europe/Volgograd",
  "Europe/Saratov",
  "Europe/Ulyanovsk",
  "Europe/Samara",
  "Asia/Yekaterinburg",
  "Asia/Omsk",
  "Asia/Novosibirsk",
  "Asia/Barnaul",
  "Asia/Tomsk",
  "Asia/Novokuznetsk",
  "Asia/Krasnoyarsk",
  "Asia/Irkutsk",
  "Asia/Chita",
  "Asia/Yakutsk",
  "Asia/Khandyga",
  "Asia/Vladivostok",
  "Asia/Ust-Nera",
  "Asia/Magadan",
  "Asia/Sakhalin",
  "Asia/Srednekolymsk",
  "Asia/Kamchatka",
  "Asia/Anadyr",
  "Asia/Riyadh",
  "Pacific/Guadalcanal",
  "Indian/Mahe",
  "Africa/Khartoum",
  "Europe/Stockholm",
  "Asia/Singapore",
  "America/Paramaribo",
  "Africa/Juba",
  "Africa/Sao_Tome",
  "America/El_Salvador",
  "Asia/Damascus",
  "America/Grand_Turk",
  "Africa/Ndjamena",
  "Indian/Kerguelen",
  "Asia/Bangkok",
  "Asia/Dushanbe",
  "Pacific/Fakaofo",
  "Asia/Dili",
  "Asia/Ashgabat",
  "Africa/Tunis",
  "Pacific/Tongatapu",
  "Europe/Istanbul",
  "America/Port_of_Spain",
  "Pacific/Funafuti",
  "Asia/Taipei",
  "Europe/Kiev",
  "Europe/Uzhgorod",
  "Europe/Zaporozhye",
  "Pacific/Wake",
  "America/New_York",
  "America/Detroit",
  "America/Kentucky/Louisville",
  "America/Kentucky/Monticello",
  "America/Indiana/Indianapolis",
  "America/Indiana/Vincennes",
  "America/Indiana/Winamac",
  "America/Indiana/Marengo",
  "America/Indiana/Petersburg",
  "America/Indiana/Vevay",
  "America/Chicago",
  "America/Indiana/Tell_City",
  "America/Indiana/Knox",
  "America/Menominee",
  "America/North_Dakota/Center",
  "America/North_Dakota/New_Salem",
  "America/North_Dakota/Beulah",
  "America/Denver",
  "America/Boise",
  "America/Phoenix",
  "America/Los_Angeles",
  "America/Anchorage",
  "America/Juneau",
  "America/Sitka",
  "America/Metlakatla",
  "America/Yakutat",
  "America/Nome",
  "America/Adak",
  "Pacific/Honolulu",
  "America/Montevideo",
  "Asia/Samarkand",
  "Asia/Tashkent",
  "America/Caracas",
  "Asia/Ho_Chi_Minh",
  "Pacific/Efate",
  "Pacific/Wallis",
  "Pacific/Apia",
  "Africa/Johannesburg"
];
